/**
 * Section Status Utilities
 *
 * Utilities for calculating section status based on uploaded images and validation results.
 *
 * Status Colors:
 * - RED: No images uploaded for this section
 * - ORANGE: Images uploaded but validation has discrepancies (missing/unexpected/mismatch items)
 * - GREEN: Images uploaded and validation passes (all expected items match detected items)
 */

import { RoomSection, HistoryItem } from '@/types';

export type SectionStatus = 'empty' | 'mismatch' | 'complete';

export interface SectionWithImages {
  section: RoomSection;
  images: HistoryItem[];
  status: SectionStatus;
}

export interface SectionValidationSummary {
  hasDiscrepancies: boolean;
  expectedCount: number;
  detectedCount: number;
  missingItems: string[];
  unexpectedItems: string[];
}

/**
 * Groups images by their section_id
 *
 * @param images - List of all images (analyses) from the session
 * @returns Map of section_id to images array (null key for unassigned images)
 */
export function groupImagesBySection(images: HistoryItem[]): Map<number | null, HistoryItem[]> {
  const grouped = new Map<number | null, HistoryItem[]>();

  for (const image of images) {
    const sectionId = image.section_id ?? null;
    if (!grouped.has(sectionId)) {
      grouped.set(sectionId, []);
    }
    grouped.get(sectionId)!.push(image);
  }

  return grouped;
}

/**
 * Calculate validation summary for a section based on its images
 *
 * This is a simplified client-side validation that counts detected items
 * and compares against expected items. For full validation, use the
 * /api/sections/validate/{section_id}/{analysis_id} endpoint.
 *
 * @param section - The section with expected items
 * @param images - Images uploaded for this section
 * @returns Validation summary with discrepancy information
 */
export function calculateSectionValidation(
  section: RoomSection,
  images: HistoryItem[]
): SectionValidationSummary {
  // Count detected items across all images in this section
  const detectedCounts = new Map<string, number>();

  for (const image of images) {
    if (image.detections) {
      for (const detection of image.detections) {
        // Only count green (ML original), purple (user corrected), blue (user drawn)
        // Skip red (user deleted) detections
        const detectionType = detection.detection_type || 'green';
        if (['green', 'purple', 'blue'].includes(detectionType)) {
          // Get class name using same logic as backend
          let className: string | undefined;

          // Try user_corrected_class first (if user corrected it)
          if (detection.user_corrected_class) {
            className = detection.user_corrected_class;
          }
          // Try top_classes[0] (standard detection result)
          else if (detection.top_classes && detection.top_classes.length > 0) {
            className = detection.top_classes[0].label || detection.top_classes[0].class_name;
          }
          // Fallback to identified_class or class_name
          else {
            className = detection.identified_class || detection.class_name;
          }

          if (className) {
            detectedCounts.set(className, (detectedCounts.get(className) || 0) + 1);
          }
        }
      }
    }
  }

  // Build expected counts map
  const expectedCounts = new Map<string, number>();
  for (const item of section.expected_items) {
    expectedCounts.set(item.class_name, item.expected_quantity);
  }

  // Find discrepancies
  const missingItems: string[] = [];
  const unexpectedItems: string[] = [];
  let hasDiscrepancies = false;

  // Check for missing or mismatched expected items
  for (const [className, expectedQty] of expectedCounts) {
    const detectedQty = detectedCounts.get(className) || 0;
    if (detectedQty === 0) {
      missingItems.push(className);
      hasDiscrepancies = true;
    } else if (detectedQty !== expectedQty) {
      hasDiscrepancies = true;
    }
  }

  // Check for unexpected items
  for (const [className, detectedQty] of detectedCounts) {
    if (!expectedCounts.has(className) && detectedQty > 0) {
      unexpectedItems.push(className);
      hasDiscrepancies = true;
    }
  }

  return {
    hasDiscrepancies,
    expectedCount: Array.from(expectedCounts.values()).reduce((a, b) => a + b, 0),
    detectedCount: Array.from(detectedCounts.values()).reduce((a, b) => a + b, 0),
    missingItems,
    unexpectedItems,
  };
}

/**
 * Calculate status for a section based on uploaded images
 *
 * @param section - The section to calculate status for
 * @param images - Images uploaded for this section
 * @returns Section status: 'empty' (red), 'mismatch' (orange), or 'complete' (green)
 */
export function calculateSectionStatus(
  section: RoomSection,
  images: HistoryItem[]
): SectionStatus {
  // No images = empty (RED)
  if (images.length === 0) {
    return 'empty';
  }

  // If section has no expected items defined, just having images makes it complete
  if (section.expected_items.length === 0) {
    return 'complete';
  }

  // Check validation
  const validation = calculateSectionValidation(section, images);

  // Has discrepancies = mismatch (ORANGE)
  if (validation.hasDiscrepancies) {
    return 'mismatch';
  }

  // All expected items match = complete (GREEN)
  return 'complete';
}

/**
 * Organize sections with their images and calculated status
 *
 * @param sections - List of all sections for the room
 * @param images - List of all images (analyses) from the session
 * @returns Array of sections with their images and status
 */
export function organizeSectionsWithStatus(
  sections: RoomSection[],
  images: HistoryItem[]
): SectionWithImages[] {
  const imagesBySection = groupImagesBySection(images);

  return sections.map(section => {
    const allSectionImages = imagesBySection.get(section.id) || [];

    // Only keep the most recent image per section (for re-upload scenarios)
    // Sort by timestamp descending and take the first one
    const sectionImages = allSectionImages.length > 0
      ? [allSectionImages.sort((a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        )[0]]
      : [];

    const status = calculateSectionStatus(section, sectionImages);

    return {
      section,
      images: sectionImages,
      status,
    };
  });
}

/**
 * Get color classes for section status
 *
 * @param status - Section status
 * @returns Tailwind CSS classes for border, background, and text colors
 */
export function getSectionStatusColors(status: SectionStatus): {
  border: string;
  background: string;
  text: string;
} {
  switch (status) {
    case 'empty':
      return {
        border: 'border-red-300',
        background: 'bg-red-50',
        text: 'text-red-700',
      };
    case 'mismatch':
      return {
        border: 'border-orange-300',
        background: 'bg-orange-50',
        text: 'text-orange-700',
      };
    case 'complete':
      return {
        border: 'border-green-300',
        background: 'bg-green-50',
        text: 'text-green-700',
      };
  }
}

/**
 * Get status label text for display
 *
 * @param status - Section status
 * @returns Human-readable status label
 */
export function getSectionStatusLabel(status: SectionStatus): string {
  switch (status) {
    case 'empty':
      return 'No images';
    case 'mismatch':
      return 'Validation issues';
    case 'complete':
      return 'Complete';
  }
}
